"use client";

import logger from '../../../utils/logger';
import requests from "../../../services/httpServices";
import { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaFileImage,
  FaFilePdf,
  FaFileVideo,
  FaFileAlt,
  FaGlobe,
  FaEllipsisV,
} from "react-icons/fa";
import { useMeetingContext } from "../../../context/MeetingContext";
import { useParams, Link } from "react-router-dom";
import { getFiles } from "../../../services/elementServices";
import { toast } from "react-toastify";
import { DeleteConfirmationModal, RenameModal } from "../../modals";

const FileView = () => {
  const {
    loading,
    setLoading,
    error,
    setError,
    setSelectedCreateElement,
    folders,
  } = useMeetingContext();

  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState("");
  const [deleteItemName, setDeleteItemName] = useState("");
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameItemId, setRenameItemId] = useState("");
  const [newName, setNewName] = useState("");
  const [viewingFolder, setViewingFolder] = useState(null);
  const [usageInfo, setUsageInfo] = useState([]);

  const { folderId } = useParams();

  // Load folder contents when component mounts or folder changes
  useEffect(() => {
    getFiles(folderId).then((response) => {
      getFiles(folderId).then((response) => {
        setFiles(response.files);
      });
    });
  }, [folderId]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowMenu(null);
    };

    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    try {
      setLoading(true);
      setError(null);

      try {
        const viewingFolder = folders.find((folder) => folder._id === folderId);
        setViewingFolder(viewingFolder);
        setFiles(viewingFolder.presets || []);
      } catch (apiError) {
        logger("warn", "API not available, using mock data:", apiError);
      }
    } catch (err) {
      setError(err.message || "Failed to load folder contents");
    } finally {
      setLoading(false);
    }
  }, [folderId, folders, setError, setLoading]);

  const handleFileSelect = (fileId) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const getIconByType = (type) => {
    switch (type) {
      case "image":
        return <FaFileImage className="w-8 h-8 text-blue-400" />;
      case "pdf":
        return <FaFilePdf className="w-8 h-8 text-red-400" />;
      case "video":
        return <FaFileVideo className="w-8 h-8 text-purple-400" />;
      case "website":
        return <FaGlobe className="w-8 h-8 text-green-400" />;
      default:
        return <FaFileAlt className="w-8 h-8 text-gray-400" />;
    }
  };

  const handleDelete = () => {
    if (deleteItemId) {
      requests.delete(`/element/delete-file/${deleteItemId}`, {
        id: deleteItemId,
      });
      toast.success("File Deleted");
      getFiles(folderId).then((response) => {
        setFiles(response.files);
      });
      setShowDeleteModal(false);
      setDeleteItemId("");
      setSelectedCreateElement("");
    }
  };

  const handleRename = (newFileName) => {
    if (renameItemId && newFileName) {
      requests.post(`/element/rename-file/${renameItemId}`, {
        newName: newFileName,
      });
      toast.success("File Renamed");

      getFiles(folderId).then((response) => {
        setFiles(response.files);
      });
      setShowRenameModal(false);
      setRenameItemId("");
      setNewName("");
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6 primary-card-background min-h-screen">
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-400 hover:text-red-300"
          >
            ×
          </button>
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <div className="flex items-center justify-between mb-6 text-white">
        <div className="flex items-center gap-2">
          <Link
            to="/dashboard/folders"
            className="flex items-center gap-2 hover:text-gray-300 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
            <span>MY FOLDERS</span>
          </Link>
          <span className="text-gray-400">/</span>
          <span className="font-medium">{viewingFolder?.name}</span>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="mt-2 text-gray-400">Loading files...</p>
        </div>
      )}

      {/* Files Grid */}
      {!loading && (
        <div className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {files.map((file) => (
              <div
                key={file._id}
                className={`
                  relative group cursor-pointer p-3 rounded-lg transition-all duration-200
                  ${
                    selectedFiles.has(file._id)
                      ? "bg-[#ffffff4b] border border-white/20"
                      : "bg-[#ffffff1f] hover:bg-gray-200/70"
                  }
                `}
                onClick={() => handleFileSelect(file._id)}
              >
                {/* File Thumbnail */}
                <div className="aspect-square mb-2 rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center">
                  {file.type === "image" && file.url ? (
                    <img
                      src={file.url}
                      alt={file.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full flex items-center justify-center ${
                      file.type === "image" && file.url ? "hidden" : ""
                    }`}
                  >
                    {getIconByType(file.type)}
                  </div>
                </div>

                {/* File Name */}
                <p className="text-xs text-white truncate text-center mb-1">
                  {file.originalName}
                </p>

                {/* 3-dot Menu Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(file._id);
                  }}
                  className="absolute bottom-2 right-2 p-1 bg-gray-700/80 hover:bg-gray-600/80 rounded opacity-0 group-hover:opacity-100 transition-opacity z-20"
                >
                  <FaEllipsisV className="w-3 h-3 text-white" />
                </button>

                {/* Menu Dropdown */}
                {showMenu === file._id && (
                  <div className="absolute right-0 bottom-0 w-30 bg-white rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(null);
                          setShowRenameModal(true);
                          setRenameItemId(file._id);
                          setNewName(file.originalName || file.name);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Rename
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMenu(null);
                          setShowDeleteModal(true);
                          setDeleteItemId(file._id);
                          setDeleteItemName(file.originalName || file.name);
                          // Mock usage data - replace with actual API call
                          setUsageInfo([
                            "Banner: Spring Offer",
                            "Banner: About company",
                            "Banner: Test",
                          ]);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}

                {/* Selection Indicator */}
                {selectedFiles.has(file.id) && (
                  <div className="absolute top-2 left-2 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          handleDelete();
          setShowDeleteModal(false);
        }}
        itemName={deleteItemName}
        usageInfo={usageInfo}
      />

      <RenameModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        onConfirm={handleRename}
        currentName={newName}
      />

      {/* {selectedCreateElement === "cta" && <CallToAction />}
      {selectedCreateElement === "media_file" && <MediaFile />}
      {selectedCreateElement === "website" && <WebsiteInput />}
      {selectedCreateElement === "document_file" && <DocumentFile />} */}
    </div>
  );
};

export default FileView;
